<template>
  <div v-if="open" class="vm-detail-panel-card">
    <div class="vm-detail-panel-header">
      <div>
        <p class="vm-detail-panel-eyebrow">Virtual Machine</p>
        <h3 class="vm-detail-panel-title">{{ title }}</h3>
      </div>
      <div class="vm-detail-header-actions">
        <a v-if="currentDetailData?.edit_url" :href="currentDetailData.edit_url" class="btn btn-sm btn-outline-primary app-action-btn app-action-btn--primary">
          <AppIcon name="edit" aria-hidden="true" />
          <span>Edit</span>
        </a>
        <button type="button" class="vm-detail-close-button" aria-label="Close" @click="$emit('close')">
          <AppIcon name="close" aria-hidden="true" />
        </button>
      </div>
    </div>
    <div class="vm-detail-panel-body" v-if="currentDetailData">
      <table class="table table-bordered table-condensed vm-detail-table">
        <tbody>
          <tr v-for="(row, index) in currentDetailData.rows" :key="`row-${index}`">
            <th><div class="text-center">{{ row.label }}</div></th>
            <td>
              <div class="text-center">
                <template v-if="row.type === 'link' && row.url">
                  <a :href="row.url">{{ row.value }}</a>
                </template>
                <template v-else>
                  {{ row.value }}
                </template>
              </div>
            </td>
          </tr>

          <template v-for="(nic, nicIndex) in currentDetailData.nics || []" :key="`nic-${nicIndex}`">
            <tr class="vm-detail-subsection-heading">
              <td colspan="2">
                <span class="vm-detail-subsection-chip">{{ nic.title }}</span>
              </td>
            </tr>
            <tr
              v-for="(row, rowIndex) in nic.rows"
              :key="`nic-${nicIndex}-row-${rowIndex}`"
              class="vm-detail-subsection-row"
            >
              <th><div class="text-center vm-detail-subsection-label">{{ row.label }}</div></th>
              <td>
                <div class="text-center">
                  <template v-if="row.type === 'ip_addresses'">
                    <div class="vm-detail-ip-addresses">
                      <div v-if="row.loading" class="vm-detail-ip-loading" aria-live="polite">
                        <AppSpinnerIcon size="sm" aria-hidden="true" />
                        <span>Loading</span>
                      </div>
                      <template v-if="row.ipv4 && row.ipv4.length">
                        <div><strong>IPv4</strong></div>
                        <div v-for="(ip, ipIndex) in row.ipv4" :key="`nic-${nicIndex}-ipv4-${ipIndex}`">{{ ip }}</div>
                      </template>

                      <template v-if="row.ipv6 && row.ipv6.length">
                        <div v-if="row.ipv4 && row.ipv4.length" class="vm-detail-address-divider"></div>
                        <div><strong>IPv6</strong></div>
                        <div v-for="(ip, ipIndex) in row.ipv6" :key="`nic-${nicIndex}-ipv6-${ipIndex}`">{{ ip }}</div>
                      </template>

                      <template v-if="(!row.ipv4 || !row.ipv4.length) && (!row.ipv6 || !row.ipv6.length)">
                        N/A
                      </template>
                    </div>
                  </template>
                  <template v-else>
                    {{ row.value }}
                  </template>
                </div>
              </td>
            </tr>
          </template>

          <tr v-for="(row, index) in currentDetailData.footer_rows || []" :key="`footer-${index}`">
            <th><div class="text-center">{{ row.label }}</div></th>
            <td>
              <div class="text-center">
                <template v-if="row.type === 'secret'">
                  <div class="text-center vm-secret-field" :data-secret-value="row.value">
                    <span class="vm-secret-value">{{ revealedSecrets[row.label] ? row.value : '••••••••' }}</span>
                    <span class="vm-secret-actions">
                      <button type="button" class="vm-secret-action" @click="toggleSecret(row.label)">
                        {{ revealedSecrets[row.label] ? 'Hide' : 'Show' }}
                      </button>
                      <button type="button" class="vm-secret-action" @click="copySecret(row.label, row.value)">
                        {{ copiedSecrets[row.label] ? 'Copied' : 'Copy' }}
                      </button>
                    </span>
                  </div>
                </template>
                <template v-else>
                  <span class="vm-detail-prewrap">{{ row.value }}</span>
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from 'vue'
import AppIcon from './AppIcon.vue'
import AppSpinnerIcon from './AppSpinnerIcon.vue'

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: ''
  },
  detailData: {
    type: Object,
    default: null
  },
  ipRefreshUrl: {
    type: String,
    default: ''
  }
})

defineEmits(['close'])

const currentDetailData = ref(null)
const revealedSecrets = ref({})
const copiedSecrets = ref({})
let refreshRequestId = 0
let ipRefreshTimer = null

function cloneData(value) {
  if (!value) {
    return null
  }

  return JSON.parse(JSON.stringify(value))
}

function toggleSecret(label) {
  revealedSecrets.value = {
    ...revealedSecrets.value,
    [label]: !revealedSecrets.value[label]
  }
}

function copySecret(label, value) {
  if (!navigator.clipboard || !value) {
    return
  }

  navigator.clipboard.writeText(value).then(() => {
    copiedSecrets.value = {
      ...copiedSecrets.value,
      [label]: true
    }

    window.setTimeout(() => {
      copiedSecrets.value = {
        ...copiedSecrets.value,
        [label]: false
      }
    }, 1200)
  })
}

function setIpLoading(loading) {
  if (!currentDetailData.value?.nics) {
    return
  }

  currentDetailData.value.nics.forEach((nic) => {
    nic.rows?.forEach((row) => {
      if (row.type === 'ip_addresses') {
        row.loading = loading
      }
    })
  })
}

function applyIpRefresh(nicIps) {
  if (!currentDetailData.value?.nics) {
    return
  }

  const ipMap = new Map((nicIps || []).map((entry) => [String(entry.if_id), entry]))

  currentDetailData.value.nics.forEach((nic) => {
    const nextIps = ipMap.get(String(nic.if_id))
    if (!nextIps) {
      return
    }

    nic.rows?.forEach((row) => {
      if (row.type === 'ip_addresses') {
        row.ipv4 = nextIps.ipv4 || []
        row.ipv6 = nextIps.ipv6 || []
        row.loading = false
      }
    })
  })
}

async function refreshIpAddresses(refreshUrl) {
  if (!refreshUrl || !currentDetailData.value?.nics?.length) {
    return
  }

  const requestId = ++refreshRequestId
  setIpLoading(true)
  const url = new URL(refreshUrl, window.location.origin)
  url.searchParams.set('_ts', String(Date.now()))

  try {
    const response = await fetch(url.toString(), {
      headers: {
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      cache: 'no-store'
    })

    if (!response.ok) {
      throw new Error(`Failed to refresh IP addresses: ${response.status}`)
    }

    const payload = await response.json()
    if (requestId !== refreshRequestId) {
      return
    }

    applyIpRefresh(payload.nic_ips || [])
  } catch (_error) {
    if (requestId === refreshRequestId) {
      setIpLoading(false)
    }
  }
}

function startIpRefreshPolling(refreshUrl) {
  stopIpRefreshPolling()
  refreshIpAddresses(refreshUrl)

  if (!refreshUrl) {
    return
  }

  ipRefreshTimer = window.setInterval(() => {
    if (!props.open || !currentDetailData.value) {
      stopIpRefreshPolling()
      return
    }

    refreshIpAddresses(refreshUrl)
  }, 5000)
}

function stopIpRefreshPolling() {
  if (ipRefreshTimer) {
    window.clearInterval(ipRefreshTimer)
    ipRefreshTimer = null
  }
}

watch(
  () => props.detailData,
  (nextValue) => {
    currentDetailData.value = cloneData(nextValue)
    revealedSecrets.value = {}
    copiedSecrets.value = {}
  },
  { immediate: true, deep: true }
)

watch(
  () => [props.open, props.ipRefreshUrl, props.detailData],
  ([isOpen, refreshUrl, detail]) => {
    if (!isOpen || !detail) {
      stopIpRefreshPolling()
      return
    }

    startIpRefreshPolling(refreshUrl)
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  stopIpRefreshPolling()
})
</script>

<style scoped>
.vm-detail-prewrap {
  white-space: pre-wrap;
  word-break: break-word;
}

.vm-detail-ip-addresses {
  position: relative;
  padding-top: 2px;
}

.vm-detail-ip-loading {
  position: absolute;
  top: 0;
  right: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #6b7280;
  font-size: 12px;
  background: rgba(255, 255, 255, 0.92);
  padding: 0 4px;
  border-radius: 999px;
}
</style>
